import React, { useState, useEffect, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Grid, ListItemText, MenuItem, Select, InputAdornment, Chip } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp, CheckCircle, PhoneIphone, Phone, ContactPhone, Build } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ShowTicketOpen from "../ShowTicketOpenModal";

const VALID_BRAZIL_DDDS = [
  "11", "12", "13", "14", "15", "16", "17", "18", "19", // SP
  "21", "22", "24", // RJ
  "27", "28", // ES
  "31", "32", "33", "34", "35", "37", "38", // MG
  "41", "42", "43", "44", "45", "46", // PR
  "47", "48", "49", // SC
  "51", "53", "54", "55", // RS
  "61", // DF
  "62", "64", // GO
  "63", // TO
  "65", "66", // MT
  "67", // MS
  "68", // AC
  "69", // RO
  "71", "73", "74", "75", "77", // BA
  "79", // SE
  "81", "87", // PE
  "82", // AL
  "83", // PB
  "84", // RN
  "85", "88", // CE
  "86", "89", // PI
  "91", "93", "94", // PA
  "92", "97", // AM
  "95", // RR
  "96", // AP
  "98", "99"  // MA
];

const formatPhoneInput = (val) => {
  if (!val) return "";
  const isPlus = val.trim().startsWith("+");
  const rawDigits = val.replace(/\D/g, "");

  // Caso 1: Número Internacional (+...) que não seja +55
  if (isPlus && !rawDigits.startsWith("55")) {
    return `+${rawDigits}`;
  }

  // Caso 2: 0800 ou 0300 (Corporativo com ou sem DDI 55)
  let tollFreeDigits = rawDigits;
  let has55Toll = false;
  if (rawDigits.startsWith("550800") || rawDigits.startsWith("550300")) {
    tollFreeDigits = rawDigits.slice(2);
    has55Toll = true;
  }
  if (tollFreeDigits.startsWith("0800") || tollFreeDigits.startsWith("0300")) {
    const prefix = tollFreeDigits.slice(0, 4);
    const rest = tollFreeDigits.slice(4);
    if (rest.length <= 3) {
      return `${has55Toll ? "+55 " : ""}${prefix}${rest ? " " + rest : ""}`;
    }
    if (rest.length <= 7) {
      return `${has55Toll ? "+55 " : ""}${prefix} ${rest.slice(0, 3)} ${rest.slice(3)}`;
    }
    return `${has55Toll ? "+55 " : ""}${prefix} ${rest.slice(0, 3)} ${rest.slice(3, 7)}`;
  }

  // Caso 3: Número com DDI 55 explícito (12 ou 13 dígitos)
  if (rawDigits.startsWith("55") && rawDigits.length > 11) {
    const ddd = rawDigits.slice(2, 4);
    const rest = rawDigits.slice(4);
    if (rest.length > 5) {
      const splitIdx = rest.length === 9 ? 5 : 4;
      return `+55 (${ddd}) ${rest.slice(0, splitIdx)}-${rest.slice(splitIdx)}`;
    } else if (rest.length > 0) {
      return `+55 (${ddd}) ${rest}`;
    }
    return `+55 (${ddd}`;
  }

  // Caso 4: Padrão nacional (DDD + Número)
  const localDigits = rawDigits.startsWith("55") && (rawDigits.length === 12 || rawDigits.length === 13)
    ? rawDigits.slice(2)
    : rawDigits;

  if (localDigits.length <= 2) {
    return localDigits.length > 0 ? `(${localDigits}` : "";
  }
  if (localDigits.length <= 6) {
    return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2)}`;
  }
  if (localDigits.length <= 10) {
    return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 6)}-${localDigits.slice(6)}`;
  }
  // 11 dígitos (Celular com 9 dígitos)
  return `(${localDigits.slice(0, 2)}) ${localDigits.slice(2, 7)}-${localDigits.slice(7, 11)}`;
};

const validateAndAnalyzePhone = (rawValue) => {
  if (!rawValue || !rawValue.trim()) {
    return {
      isValid: false,
      error: "Informe o número de telefone com DDD.",
      type: "empty",
      cleanNumber: ""
    };
  }

  const isPlus = rawValue.trim().startsWith("+");
  const digits = rawValue.replace(/\D/g, "");

  // 1. Validação de Números 0800 / 0300
  let tollFree = digits;
  if (digits.startsWith("550800") || digits.startsWith("550300")) {
    tollFree = digits.slice(2);
  }
  if (tollFree.startsWith("0800") || tollFree.startsWith("0300")) {
    const prefix = tollFree.slice(0, 4);
    if (tollFree.length < 11) {
      return {
        isValid: false,
        error: `Número ${prefix} incompleto! Deve conter 11 dígitos (ex: ${prefix} 123 4567).`,
        type: "tollfree_incomplete",
        cleanNumber: `55${tollFree}`
      };
    }
    if (tollFree.length === 11) {
      return {
        isValid: true,
        error: null,
        type: "tollfree",
        cleanNumber: `55${tollFree}`,
        infoLabel: `${prefix} Corporativo`
      };
    }
    return {
      isValid: false,
      error: `Número ${prefix} com dígitos em excesso.`,
      type: "excess_digits",
      cleanNumber: `55${tollFree}`
    };
  }

  // 2. Validação de Números Internacionais (+...)
  if (isPlus && !digits.startsWith("55")) {
    if (digits.length < 8) {
      return {
        isValid: false,
        error: "Número internacional incompleto.",
        type: "international",
        cleanNumber: digits
      };
    }
    return {
      isValid: true,
      error: null,
      type: "international",
      cleanNumber: digits,
      infoLabel: "Internacional"
    };
  }

  // 3. Normalização Nacional
  let nationalDigits = digits;
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    nationalDigits = digits.slice(2);
  }

  if (nationalDigits.length < 8) {
    return {
      isValid: false,
      error: "Número de telefone incompleto! Digite o DDD + número.",
      type: "incomplete",
      cleanNumber: digits
    };
  }

  if (nationalDigits.length === 8 || nationalDigits.length === 9) {
    return {
      isValid: false,
      error: "Falta o DDD! Informe os 2 dígitos do DDD antes do número (ex: (66) 98152-6545).",
      type: "missing_ddd",
      cleanNumber: digits
    };
  }

  const ddd = nationalDigits.slice(0, 2);
  if (!VALID_BRAZIL_DDDS.includes(ddd)) {
    return {
      isValid: false,
      error: `O DDD (${ddd}) não é válido no Brasil. Verifique o DDD digitado.`,
      type: "invalid_ddd",
      cleanNumber: digits
    };
  }

  const numberWithoutDdd = nationalDigits.slice(2);

  // 4. Cenário de 10 DÍGITOS (DDD + 8 dígitos)
  if (nationalDigits.length === 10) {
    const firstDigit = numberWithoutDdd[0];

    // Se começa com 6, 7, 8 ou 9, é celular faltando o 9 inicial!
    if (["6", "7", "8", "9"].includes(firstDigit)) {
      const formattedSuggested = `(${ddd}) 9${numberWithoutDdd.slice(0, 4)}-${numberWithoutDdd.slice(4)}`;
      return {
        isValid: false,
        error: `Celular faltando o dígito 9! O padrão brasileiro exige o 9 na frente: ${formattedSuggested}`,
        type: "cell_missing_9",
        cleanNumber: `55${ddd}9${numberWithoutDdd}`,
        suggestedFormatted: formattedSuggested,
        canAutoFix: true
      };
    }

    // Telefone fixo no Brasil: começa com 2, 3, 4 ou 5
    if (["2", "3", "4", "5"].includes(firstDigit)) {
      return {
        isValid: true,
        error: null,
        type: "landline",
        cleanNumber: `55${nationalDigits}`,
        infoLabel: `Fixo (DDD ${ddd})`
      };
    }

    return {
      isValid: false,
      error: "Número fixo inválido! Telefones fixos no Brasil começam com 2, 3, 4 ou 5.",
      type: "invalid_landline",
      cleanNumber: `55${nationalDigits}`
    };
  }

  // 5. Cenário de 11 DÍGITOS (DDD + 9 dígitos - Celular)
  if (nationalDigits.length === 11) {
    const firstDigit = numberWithoutDdd[0];
    const secondDigit = numberWithoutDdd[1];

    if (firstDigit !== "9") {
      return {
        isValid: false,
        error: "Número de celular inválido! Celulares no Brasil devem iniciar com 9 após o DDD.",
        type: "invalid_cellphone",
        cleanNumber: `55${nationalDigits}`
      };
    }

    // No Brasil, celulares iniciam com 9 seguido de 6, 7, 8 ou 9 (ex: 98xxx, 99xxx, 97xxx, 96xxx)
    if (!["6", "7", "8", "9"].includes(secondDigit)) {
      return {
        isValid: false,
        error: "Número de celular inválido! Celulares no Brasil iniciam com 98, 99, 97 ou 96.",
        type: "invalid_cellphone_prefix",
        cleanNumber: `55${nationalDigits}`
      };
    }

    return {
      isValid: true,
      error: null,
      type: "cellphone",
      cleanNumber: `55${nationalDigits}`,
      infoLabel: `Celular (DDD ${ddd})`
    };
  }

  return {
    isValid: false,
    error: "Número com dígitos em excesso. Verifique o número digitado.",
    type: "excess_digits",
    cleanNumber: digits
  };
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: 440,
    width: "100%",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0,0,0,0.18)"
  },
  dialogTitle: {
    padding: theme.spacing(2, 2.5, 1.5),
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #f0f0f0",
    "& h2": {
      fontSize: 18,
      fontWeight: 700,
      color: "#1e293b",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between"
    }
  },
  dialogContent: {
    padding: theme.spacing(2.5),
    paddingTop: theme.spacing(2)
  },
  compactField: {
    "& .MuiInputLabel-outlined": {
      fontSize: 13,
      fontWeight: 500
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)"
    },
    "& .MuiOutlinedInput-root": {
      minHeight: 44,
      fontSize: 14,
      borderRadius: 8,
      backgroundColor: "#fff",
      transition: "border-color 0.2s ease"
    },
    "& .MuiOutlinedInput-input": {
      paddingTop: 11,
      paddingBottom: 11
    }
  },
  captionHelper: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 1.3,
    color: "#64748b"
  },
  autoFixButton: {
    marginTop: 6,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    color: "#0284c7",
    borderColor: "#bae6fd",
    backgroundColor: "#f0f9ff",
    "&:hover": {
      backgroundColor: "#e0f2fe",
      borderColor: "#0284c7"
    }
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: "#fafafa",
    borderTop: "1px solid #f0f0f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8
  },
  cancelButton: {
    minHeight: 40,
    minWidth: 100,
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    color: "#ef4444",
    borderColor: "#fca5a5",
    "&:hover": {
      borderColor: "#ef4444",
      backgroundColor: "rgba(239, 68, 68, 0.04)"
    }
  },
  saveButton: {
    minHeight: 40,
    minWidth: 110,
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    backgroundColor: "#0ea5e9",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#0284c7"
    }
  }
}));

const NewTicketModal = ({ modalOpen, onClose, initialContact }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");

  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);

  const { user } = useContext(AuthContext);
  const companyId = user.companyId || user.company_id;
  const whatsappId = user.whatsappId || user.whatsapp_id;
  const isAdminLike = user?.profile === "admin" || user?.profile === "super_admin" || user?.super;

  const [whatsapps, setWhatsapps] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  const [searchingNumber, setSearchingNumber] = useState(false);
  const [existingContact, setExistingContact] = useState(null);

  // Análise de telefone em tempo real
  const phoneAnalysis = validateAndAnalyzePhone(contactNumber);

  useEffect(() => {
    if (initialContact?.id || initialContact?.number) {
      setContactName(initialContact.name || "");
      setContactNumber(formatPhoneInput(initialContact.number || ""));
      if (initialContact.id) {
        setExistingContact(initialContact);
      }
    }
  }, [initialContact]);

  // Carrega conexões de WhatsApp disponíveis
  useEffect(() => {
    if (modalOpen) {
      setLoading(true);
      const fetchWhatsapps = async () => {
        try {
          const { data: whatsappData } = await api.get(`/whatsapp`, { params: { companyId, session: 0 } });
          const allUserIds = [
            ...(Array.isArray(user?.whatsappIds) ? user.whatsappIds : []),
            ...(user?.whatsappId ? [Number(user.whatsappId)] : [])
          ]
            .map(Number)
            .filter(Boolean)
            .filter((id, idx, arr) => arr.indexOf(id) === idx);

          const visibleWhatsapps = isAdminLike
            ? whatsappData
            : (allUserIds.length > 0
              ? whatsappData.filter(wpp => allUserIds.includes(Number(wpp.id)))
              : []);

          setWhatsapps(visibleWhatsapps);

          let defaultWaId = "";
          if (visibleWhatsapps.length === 1) {
            defaultWaId = visibleWhatsapps[0].id;
          } else if (whatsappId && visibleWhatsapps.some(w => Number(w.id) === Number(whatsappId))) {
            defaultWaId = whatsappId;
          } else if (visibleWhatsapps.length > 0) {
            defaultWaId = visibleWhatsapps[0].id;
          }

          setSelectedWhatsapp(defaultWaId);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchWhatsapps();
    }
  }, [modalOpen, companyId, whatsappId, user, isAdminLike]);

  // Função para carregar etapas do CRM da conexão selecionada
  const fetchStagesForWhatsapp = useCallback(async (waId) => {
    if (!waId && waId !== 0) {
      setStages([]);
      setSelectedStage("");
      return;
    }
    setLoadingStages(true);
    try {
      const { data: stageData } = await api.get("/crm/stages", { params: { whatsappId: waId } });
      const stageList = Array.isArray(stageData) ? stageData : [];
      setStages(stageList);

      if (stageList.length > 0) {
        // Seleciona por padrão a etapa inicial ("Novos leads" ou primeira etapa)
        const novosLeads = stageList.find(q => {
          const name = (q.name || "").toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          return name.includes("NOVO") || name.includes("LEAD");
        });
        setSelectedStage(novosLeads ? novosLeads.id : stageList[0].id);
      } else {
        setSelectedStage("");
      }
    } catch (err) {
      setStages([]);
      setSelectedStage("");
    } finally {
      setLoadingStages(false);
    }
  }, []);

  // Recarrega etapas sempre que a conexão selecionada mudar
  useEffect(() => {
    if (modalOpen && selectedWhatsapp) {
      fetchStagesForWhatsapp(selectedWhatsapp);
    }
  }, [modalOpen, selectedWhatsapp, fetchStagesForWhatsapp]);

  // Busca em tempo real por duplicidade de número
  useEffect(() => {
    const rawDigits = contactNumber.replace(/\D/g, "");
    if (rawDigits.length < 8) {
      setExistingContact(null);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setSearchingNumber(true);
      try {
        const { data } = await api.get(`/contact-by-number/${rawDigits}`);
        if (data) {
          setExistingContact(data);
          if (!contactName) {
            setContactName(data.name || "");
          }
          toast.info(`Contato encontrado: ${data.name}`);
        } else {
          setExistingContact(null);
        }
      } catch (err) {
        setExistingContact(null);
      } finally {
        setSearchingNumber(false);
      }
    }, 600);

    return () => clearTimeout(delayDebounceFn);
  }, [contactNumber, contactName]);

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setContactNumber(formatted);
  };

  const handleAutoFixNine = () => {
    if (phoneAnalysis.suggestedFormatted) {
      setContactNumber(phoneAnalysis.suggestedFormatted);
      toast.success("Dígito 9 inserido com sucesso!");
    }
  };

  const handleClose = () => {
    onClose();
    setContactName("");
    setContactNumber("");
    setSelectedStage("");
    setSelectedWhatsapp("");
    setExistingContact(null);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
  };

  const handleSaveTicket = async () => {
    // 1. Validação estrita do Telefone antes de qualquer chamada
    const analysis = validateAndAnalyzePhone(contactNumber);
    if (!analysis.isValid) {
      toast.error(analysis.error || "Número de telefone inválido!");
      return;
    }

    // 2. Validação da Conexão
    if (!selectedWhatsapp) {
      toast.error("Selecione a conexão do WhatsApp para iniciar o atendimento.");
      return;
    }

    // 3. Validação da Etapa do CRM
    if (stages.length > 0 && !selectedStage) {
      toast.error("Selecione a etapa do CRM.");
      return;
    }

    setLoading(true);
    try {
      let contactId = existingContact?.id;
      const normalizedNumber = analysis.cleanNumber;

      // Se não existe, cria o contato primeiro
      if (!contactId) {
        try {
          const { data: createdContact } = await api.post("/contacts", {
            name: (contactName || "").trim() || contactNumber,
            number: normalizedNumber,
            whatsapp_id: selectedWhatsapp || null,
          });
          contactId = createdContact.id;
        } catch (err) {
          if (err.response?.data?.error?.includes("CONTATO_EXISTE")) {
            const parts = err.response.data.error.split(":");
            contactId = parts[2];
          } else {
            throw err;
          }
        }
      }

      // Cria o ticket já atribuindo ao usuário autenticado e conexão
      const { data: ticket } = await api.post("/tickets", {
        contact_id: contactId,
        queue_id: null,
        whatsapp_id: selectedWhatsapp || null,
        userId: user.id,
        status: "open",
      });

      // Move o ticket diretamente para a etapa do CRM selecionada
      if (ticket?.id && selectedStage) {
        try {
          await api.post("/crm/stages/move-ticket", {
            ticket_id: ticket.id,
            crm_stage_id: selectedStage,
          });
        } catch (e) {
          // Etapa silenciosa se falhar
        }
      }

      handleClose();
      history.push(`/tickets/${ticket.uuid}`);
    } catch (err) {
      if (err.response?.data?.error) {
        try {
          const ticketError = JSON.parse(err.response.data.error);
          if (ticketError.userId !== user?.id) {
            setOpenAlert(true);
            setUserTicketOpen(ticketError?.user?.name);
            setQueueTicketOpen(ticketError?.queue?.name);
          } else {
            handleClose();
            history.push(`/tickets/${ticketError.uuid}`);
          }
        } catch (e) {
          toastError(err);
        }
      } else {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
      default:
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
    }
  };

  const getPhoneIcon = () => {
    if (phoneAnalysis.type === "cellphone") {
      return <PhoneIphone style={{ fontSize: 14 }} />;
    }
    if (phoneAnalysis.type === "landline") {
      return <Phone style={{ fontSize: 14 }} />;
    }
    if (phoneAnalysis.type === "tollfree") {
      return <ContactPhone style={{ fontSize: 14 }} />;
    }
    return <WhatsApp style={{ fontSize: 14 }} />;
  };

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography variant="h6" component="div" style={{ fontWeight: 700, color: "#0f172a" }}>
            Novo Atendimento
          </Typography>
          {phoneAnalysis.isValid && (
            <Chip
              size="small"
              icon={getPhoneIcon()}
              label={phoneAnalysis.infoLabel}
              style={{
                backgroundColor: "#ecfdf5",
                color: "#059669",
                fontWeight: 600,
                fontSize: 11
              }}
            />
          )}
        </DialogTitle>

        <DialogContent dividers className={classes.dialogContent}>
          <Grid container spacing={2}>
            {/* CONEXÃO DO WHATSAPP (Prioritária para filtrar as etapas do CRM) */}
            <Grid item xs={12}>
              <Select
                className={classes.compactField}
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedWhatsapp}
                onChange={(e) => setSelectedWhatsapp(e.target.value)}
                disabled={!isAdminLike && whatsapps.length <= 1}
                renderValue={() => {
                  if (selectedWhatsapp === "") return <span style={{ color: "#94a3b8" }}>Selecione a conexão do WhatsApp</span>;
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp);
                  return whatsapp ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {IconChannel(whatsapp.channel)}
                      <span>{whatsapp.name}</span>
                    </div>
                  ) : "Selecione uma conexão";
                }}
              >
                {whatsapps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    <ListItemText
                      primary={
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {IconChannel(whatsapp.channel)}
                          <Typography component="span" style={{ fontSize: 14 }}>
                            {whatsapp.name}
                          </Typography>
                        </div>
                      }
                    />
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* NÚMERO DO WHATSAPP */}
            <Grid item xs={12}>
              <TextField
                className={classes.compactField}
                fullWidth
                label="Número do WhatsApp (com DDD)"
                placeholder="(66) 98152-6545 ou 0800..."
                variant="outlined"
                value={contactNumber}
                onChange={handlePhoneChange}
                autoFocus
                error={Boolean(contactNumber && !phoneAnalysis.isValid)}
                helperText={contactNumber && !phoneAnalysis.isValid ? phoneAnalysis.error : ""}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsApp style={{ color: phoneAnalysis.isValid ? "#25d366" : "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchingNumber ? (
                    <InputAdornment position="end">
                      <CircularProgress size={18} />
                    </InputAdornment>
                  ) : existingContact ? (
                    <InputAdornment position="end">
                      <CheckCircle style={{ color: "#10b981", fontSize: 20 }} />
                    </InputAdornment>
                  ) : null
                }}
              />

              {/* Botão de auto-correção quando faltar o dígito 9 */}
              {phoneAnalysis.canAutoFix && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Build style={{ fontSize: 14 }} />}
                  className={classes.autoFixButton}
                  onClick={handleAutoFixNine}
                >
                  Corrigir para celular: {phoneAnalysis.suggestedFormatted}
                </Button>
              )}
            </Grid>

            {/* NOME DO CONTATO */}
            <Grid item xs={12}>
              <TextField
                className={classes.compactField}
                fullWidth
                label="Nome do Contato (Opcional)"
                placeholder="Ex: Maria da Silva"
                variant="outlined"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </Grid>

            {/* ETAPA DO CRM (Filtrada pela conexão selecionada) */}
            <Grid item xs={12}>
              <Select
                className={classes.compactField}
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                disabled={loadingStages || stages.length === 0}
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "center",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "center",
                  },
                  PaperProps: {
                    style: {
                      maxHeight: 280,
                      width: "min(400px, calc(100vw - 48px))",
                      borderRadius: 8
                    }
                  }
                }}
                renderValue={() => {
                  if (loadingStages) return <span style={{ color: "#94a3b8" }}>Carregando etapas...</span>;
                  if (stages.length === 0) return <span style={{ color: "#94a3b8" }}>Nenhuma etapa de CRM nesta conexão</span>;
                  if (selectedStage === "") return <span style={{ color: "#94a3b8" }}>Selecione a etapa do CRM</span>;
                  const stage = stages.find(s => s.id === selectedStage);
                  return stage ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {stage.color && (
                        <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: stage.color, flexShrink: 0 }} />
                      )}
                      <span>{stage.name}</span>
                    </div>
                  ) : "Selecione a etapa do CRM";
                }}
              >
                {stages?.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    <ListItemText primary={stage.name} />
                    {stage.color && (
                      <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: stage.color, marginLeft: 8, flexShrink: 0 }} />
                    )}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" className={classes.captionHelper}>
                Selecione em qual etapa do CRM este atendimento será iniciado.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
            className={classes.cancelButton}
          >
            Cancelar
          </Button>
          <ButtonWithSpinner
            className={classes.saveButton}
            variant="contained"
            type="button"
            onClick={handleSaveTicket}
            loading={loading}
          >
            Iniciar Atendimento
          </ButtonWithSpinner>
        </DialogActions>

        {openAlert && (
          <ShowTicketOpen
            isOpen={openAlert}
            handleClose={handleCloseAlert}
            user={userTicketOpen}
            queue={queueTicketOpen}
          />
        )}
      </Dialog>
    </>
  );
};

export default NewTicketModal;
